import { Combobox } from '@monorepo/shared/componentsV2/Combobox';
import { getFieldNameWithPrefix } from '@monorepo/shared/componentsV2/fields/fieldUtils';
import { SelectableOption } from '@monorepo/shared/types/SelectableOption';
import {
  compose,
  hasNoInvalidCharacters,
  isRequired,
} from '@monorepo/shared/utils/validators';
import {
  FormConditionalOperator,
  SelectFormFieldOptions,
} from 'mapistry-shared';
import React, { useMemo } from 'react';
import { useField } from 'react-final-form';
import styled from 'styled-components';
import { isContainsMatchOperator } from '../../../conditionalOperatorConsts';
import { type ConditionalFieldValueInputProps } from './ConditionalFieldValueFactory';
import { inputWithWhiteBackground } from './styled';

const canSatisfyConditionalRules = (options: string[]) => (value: string) => {
  if (options.every((option) => !option.trim().includes(value.trim()))) {
    return 'None of the available options contain this text';
  }
  return undefined;
};

const StyledCombobox = styled(Combobox)<{ $isErrorTextShown: boolean }>`
  ${inputWithWhiteBackground}
`;

export function ConditionalSelectField({
  options,
  rulePrefix,
  inputFieldProps: { name, ...inputFieldProps },
}: {
  options: SelectFormFieldOptions;
  rulePrefix: string;
  inputFieldProps: ConditionalFieldValueInputProps['inputFieldProps'];
}) {
  const {
    input: { onChange, value },
  } = useField<string>(name, {
    validate: compose(
      isRequired,
      hasNoInvalidCharacters('|'),
      canSatisfyConditionalRules(options),
    ),
  });

  const {
    input: { value: operator },
  } = useField<FormConditionalOperator>(
    getFieldNameWithPrefix('operator', rulePrefix),
  );

  const {
    input: { value: selectedValuesList },
  } = useField<(string | undefined)[]>(
    getFieldNameWithPrefix('conditionalFieldValues', rulePrefix),
  );

  const selectableOptions: SelectableOption<string>[] = useMemo(
    () =>
      options
        .filter(
          (option: string) =>
            // Filter out the selected values in other fields (but not its own value)
            !selectedValuesList.map((v) => v?.trim()).includes(option.trim()) ||
            option.trim() === value.trim(),
        )
        .map((option: string) => ({
          label: option.trim(),
          value: option.trim(),
        })),
    [options, selectedValuesList, value],
  );

  return (
    <StyledCombobox
      {...inputFieldProps}
      allowCustomValue={isContainsMatchOperator(operator)}
      disableClearable
      error={inputFieldProps.error}
      errorText={inputFieldProps.errorText}
      fullWidth
      dontSaveSpaceForErrorText
      name={name}
      onChange={onChange}
      options={selectableOptions}
      value={value}
      $isErrorTextShown={!!inputFieldProps.errorText}
    />
  );
}
